import React, {
  ReactText,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import {
  towns,
  getTownName,
  getTownComarca,
  getTownGentilici,
  getTownPoblacio2025,
  getTownPoblacio2024,
  getTownPoblacio2023,
  getTownPoblacio2022,
  getTownPoblacio2021,
  getTownPoblacio2008,
  getTownPoblacio1996,
  sanitizeTownName,
} from "../domain/towns";
import { TownInput } from "./TownInput";
import * as geolib from "geolib";
import { Share } from "./Share";
import { Guesses } from "./Guesses";
import { useTranslation } from "react-i18next";
import { SettingsData } from "../hooks/useSettings";
import { useMode } from "../hooks/useMode";
import { getDayString, useTodays } from "../hooks/useTodays";
import { Twemoji } from "react-emoji-render";
import { getComarcaName } from "../domain/comarques";
import ReactGA from 'react-ga';
import ReactGA4 from 'react-ga4';

ReactGA.initialize('UA-109671963-1'); 
ReactGA4.initialize('G-T7BE8SGHQY'); 

const MAX_TRY_COUNT = 6;

interface GameProps {
  settingsData: SettingsData;
  updateSettings: (newSettings: Partial<SettingsData>) => void;
}

export function Game({ settingsData, updateSettings }: GameProps) {
  const { t, i18n } = useTranslation();
  const dayString = useMemo(
    () => getDayString(settingsData.shiftDayCount),
    [settingsData.shiftDayCount]
  );

  const townInputRef = useRef<HTMLInputElement>(null);

  const [todays, addGuess, randomAngle, imageScale] = useTodays(dayString);
  const { town, guesses } = todays;

  const [currentGuess, setCurrentGuess] = useState("");
  const [hideImageMode, setHideImageMode] = useMode(
    "hideImageMode",
    dayString,
    settingsData.noImageMode
  );
  const [rotationMode, setRotationMode] = useMode(
    "rotationMode",
    dayString,
    settingsData.rotationMode
  );

  const gameEnded =
    guesses.length === MAX_TRY_COUNT ||
    guesses[guesses.length - 1]?.distance === 0;

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      if (town == null) {
        return;
      }
      e.preventDefault();
      const guessedTown = towns.find(
        (town) =>
          sanitizeTownName(
            getTownName(i18n.resolvedLanguage, town)
          ) === sanitizeTownName(currentGuess)
      );

      if (guessedTown == null) {
        toast.error(t("unknownTown"));
        return;
      }

      const newGuess = {
        name: currentGuess,
        distance: geolib.getDistance(guessedTown, town),
        direction: geolib.getCompassDirection(
          guessedTown,
          town,
          (origin, dest) =>
            Math.round(geolib.getRhumbLineBearing(origin, dest) / 45) * 45
        ),
      };

      if (guesses.length === 0) {
        ReactGA.event({
          category: 'Partida PobleCV',
          action: 'Partida iniciada',
          label: dayString + ': ' + getTownName(i18n.resolvedLanguage, town)
        });
        ReactGA4.event({
          category: 'Partida PobleCV',
          action: 'Partida iniciada',
          label: dayString + ': ' + getTownName(i18n.resolvedLanguage, town)
        });
      }

      if (newGuess.distance === 0) {
        toast.success(t("welldone"), { delay: 2000 });

        ReactGA.event({
          category: 'Partida PobleCV',
          action: 'Poble encertat', 
          label: dayString + ': ' + getTownName(i18n.resolvedLanguage, town) + ' [Intents: ' + (guesses.length + 1) + '/' + MAX_TRY_COUNT + ']'
        });
        ReactGA4.event({
          category: 'Partida PobleCV',
          action: 'Poble encertat', 
          label: dayString + ': ' + getTownName(i18n.resolvedLanguage, town) + ' [Intents: ' + (guesses.length + 1) + '/' + MAX_TRY_COUNT + ']'
        });
      } else if(guesses.length === (MAX_TRY_COUNT - 1)) {
          ReactGA.event({
          category: 'Partida PobleCV',
          action: 'Partida perduda', 
          label: dayString + ': ' + getTownName(i18n.resolvedLanguage, town)
        });
        ReactGA4.event({
          category: 'Partida PobleCV',
          action: 'Partida perduda', 
          label: dayString + ': ' + getTownName(i18n.resolvedLanguage, town)
        });
        }

      addGuess(newGuess);
      setCurrentGuess("");
    },
    [addGuess, town, currentGuess, i18n.resolvedLanguage, t]
  );

  useEffect(() => {
    let toastId: ReactText;
    const { town, guesses } = todays;
    if (
      town &&
      guesses.length === MAX_TRY_COUNT &&
      guesses[guesses.length - 1].distance > 0
    ) {
      toastId = toast.info(
        getTownName(i18n.resolvedLanguage, town).toUpperCase(),
        {
          autoClose: false,
          delay: 2000,
        }
      );
    }

    return () => {
      if (toastId != null) {
        toast.dismiss(toastId);
      }
    };
  }, [todays, i18n.resolvedLanguage]);

  return (
    <div className="flex-grow flex flex-col mx-2">
      {hideImageMode && !gameEnded && (
        <button
          className="border-2 uppercase my-2 hover:bg-gray-50 active:bg-gray-100 dark:hover:bg-slate-800 dark:active:bg-slate-700"
          type="button"
          onClick={() => setHideImageMode(false)}
        >
          <Twemoji
            text={t("showTown")}
            options={{ className: "inline-block" }}
          />
        </button>
      )}
      <div className="flex my-1">
        {settingsData.allowShiftingDay && settingsData.shiftDayCount > 0 && (
          <button
            type="button"
            onClick={() =>
              updateSettings({
                shiftDayCount: Math.max(0, settingsData.shiftDayCount - 1),
              })
            }
          >
            <Twemoji text="↪️" className="text-xl" />
          </button>
        )}
        <img
          className={`pointer-events-none max-h-52 m-auto transition-transform duration-700 ease-in dark:invert ${
            hideImageMode && !gameEnded ? "h-0" : "h-full"
          }`}
          alt="Poble a endevinar"
          src={`images/towns/${town?.code.toLowerCase()}.svg`}
          style={
            rotationMode && !gameEnded
              ? {
                  transform: `rotate(${randomAngle}deg) scale(${imageScale})`,
                }
              : {}
          }
        />
        {settingsData.allowShiftingDay && settingsData.shiftDayCount < 7 && (
          <button
            type="button"
            onClick={() =>
              updateSettings({
                shiftDayCount: Math.min(7, settingsData.shiftDayCount + 1),
              })
            }
          >
            <Twemoji text="↩️" className="text-xl" />
          </button>
        )}
      </div>
      {rotationMode && !hideImageMode && !gameEnded && (
        <button
          className="border-2 uppercase mb-2 hover:bg-gray-50 active:bg-gray-100 dark:hover:bg-slate-800 dark:active:bg-slate-700"
          type="button"
          onClick={() => setRotationMode(false)}
        >
          <Twemoji
            text={t("cancelRotation")}
            options={{ className: "inline-block" }}
          />
        </button>
      )}
      <Guesses
        rowCount={MAX_TRY_COUNT}
        guesses={guesses}
        settingsData={settingsData}
        townInputRef={townInputRef}
      />
      <div className="my-2">
        {gameEnded && town ? (
          <>
            <Share
              guesses={guesses}
              dayString={dayString}
              settingsData={settingsData}
              hideImageMode={hideImageMode}
              rotationMode={rotationMode}
            />
            <div className="border-2 pt-2 pb-2 border-green-600 w-full text-center block mt-4 text-xs">
              <p className="text-sm"><span className="font-bold uppercase">{getTownName(
                i18n.resolvedLanguage,
                town
              )}</span> ({getComarcaName(i18n.resolvedLanguage, getTownComarca(i18n.resolvedLanguage, town))})</p>
              <p><b>Gentilici:</b> {getTownGentilici(
                i18n.resolvedLanguage,
                town
              )}</p>
              <p>
              {getTownPoblacio2022(i18n.resolvedLanguage,town) > 0 ? (
                  <>
                  <span><b>Població 2025:</b> {getTownPoblacio2025(
                  i18n.resolvedLanguage,
                  town
                  ).toLocaleString("es")}</span>
                  </>
                ) : (<></>)
              }
              {getTownPoblacio2008(i18n.resolvedLanguage,town) > 0 ? (
                  <>
                  <span> | <br></br><b>2008:</b> {getTownPoblacio2008(
                  i18n.resolvedLanguage,
                  town
                  ).toLocaleString("es")}</span>
                  </>
                ) : (<></>)
              }
              {getTownPoblacio1996(i18n.resolvedLanguage,town) > 0 ? (
                  <>
                  <span> | <b>1996:</b> {getTownPoblacio1996(
                  i18n.resolvedLanguage,
                  town
                  ).toLocaleString("es")}</span>
                  </>
                ) : (<></>)
              }
              </p>
              <a
              className="underline w-full text-center block mt-2"
              href={`https://www.google.com/maps?q=${getTownName(
                i18n.resolvedLanguage,
                town
              )}+Comunitat Valenciana&hl=${i18n.resolvedLanguage}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Twemoji
                text={t("showOnGoogleMaps")}
                options={{ className: "inline-block" }}
              />
            </a>
            </div>
            

            <a
              className="underline w-full text-center block mt-3"
              href={`https://www.apuntmedia.es/resultats/\"${getTownName(
                i18n.resolvedLanguage,
                town
              )}\"`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Twemoji
                text={t("showOnApuntWeb")}
                options={{ className: "inline-block" }}
              />
              {getTownName(
                i18n.resolvedLanguage,
                town
              )}
              <Twemoji
                text={t("showOnApuntWeb2")}
                options={{ className: "inline-block" }}
              />
              
            </a>

            <button
              className="rounded font-semibold p-1 flex text-center items-center justify-center border-2 uppercase my-0.5 text-white bg-green-600 hover:bg-green-500 active:bg-green-700 w-full mt-4"
              onClick={() => window.open('https://escuts.apuntmedia.es', '_blank')}
            >
              <Twemoji
                text="🛡️"
                options={{ className: "inline-block" }}
                className="flex items-center justify-center"
              />{" "}
              <span className="ml-1">{t("escutsGame")}</span>
            </button>

            <button
                className="rounded font-semibold p-1 flex text-center items-center justify-center border-2 uppercase my-0.5 bg-orange-400 hover:bg-amber-500 active:bg-amber-500 dark:hover:bg-amber-500 dark:active:bg-amber-500 dark:hover:bg-slate-800 dark:active:bg-slate-700 w-full"
                onClick={() => window.open('https://etsona.apuntmedia.es', '_blank')}
              >

                <Twemoji
                  text="🎵"
                  options={{ className: "inline-block" }}
                  className="flex items-center justify-center"
                />{" "}
                <span className="ml-1">{t("musicGame")}</span>
            </button>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="flex flex-col">
              <TownInput
                inputRef={townInputRef}
                currentGuess={currentGuess}
                setCurrentGuess={setCurrentGuess}
              />
              <button
                className="rounded font-bold p-1 flex items-center justify-center border-2 uppercase my-0.5 hover:bg-gray-50 active:bg-gray-100 dark:hover:bg-slate-800 dark:active:bg-slate-700"
                type="submit"
              >

                <Twemoji
                  text="🔎"
                  options={{ className: "inline-block" }}
                  className="flex items-center justify-center"
                />{" "}
                <span className="ml-1">{t("guess")}</span>
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
